// client/src/realtime/socketClient.ts
// WebSocket client for real-time communication

import {
  RealtimeEventType,
  RealtimeEventTypeValue,
  ClientMessageType,
  RealtimeEvent,
  ClientMessage,
  SystemMessage,
} from './events';

type EventCallback<T = unknown> = (payload: T) => void;
type ConnectionCallback = () => void;

interface Subscription {
  topic: string;
  eventType?: RealtimeEventTypeValue;
  callback: EventCallback;
}

interface RealtimeClientConfig {
  url?: string;
  reconnectAttempts?: number;
  reconnectBaseDelay?: number;
  heartbeatInterval?: number;
  debug?: boolean;
}

class RealtimeClient {
  private socket: WebSocket | null = null;
  private connectionId: string | null = null;
  private subscriptions: Subscription[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts: number;
  private reconnectBaseDelay: number;
  private heartbeatInterval: number;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private url: string;
  private debug: boolean;
  private isManualDisconnect = false;
  private pendingSubscriptions: string[] = [];
  private connectionCallbacks: ConnectionCallback[] = [];
  private disconnectionCallbacks: ConnectionCallback[] = [];

  constructor(config: RealtimeClientConfig = {}) {
    // Determine WebSocket URL based on current location
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const defaultUrl = `${wsProtocol}//${window.location.host}/ws`;

    this.url = config.url || defaultUrl;
    this.maxReconnectAttempts = config.reconnectAttempts ?? 10;
    this.reconnectBaseDelay = config.reconnectBaseDelay ?? 1000;
    this.heartbeatInterval = config.heartbeatInterval ?? 30000;
    this.debug = config.debug ?? false;
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[Realtime]', ...args);
    }
  }

  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.log('Already connected');
      return;
    }

    if (this.socket?.readyState === WebSocket.CONNECTING) {
      this.log('Connection in progress');
      return;
    }

    this.isManualDisconnect = false;
    this.log('Connecting to', this.url);

    try {
      this.socket = new WebSocket(this.url);
      this.setupSocketHandlers();
    } catch (error) {
      this.log('Connection error:', error);
      this.scheduleReconnect();
    }
  }

  private setupSocketHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.log('Connected');
      this.reconnectAttempts = 0;
      this.startHeartbeat();
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        this.log('Failed to parse message:', error);
      }
    };

    this.socket.onclose = (event) => {
      this.log('Disconnected:', event.code, event.reason);
      this.connectionId = null;
      this.stopHeartbeat();
      this.disconnectionCallbacks.forEach((cb) => cb());

      if (!this.isManualDisconnect) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      this.log('Socket error:', error);
    };
  }

  private handleMessage(data: SystemMessage | RealtimeEvent): void {
    // Handle system messages
    if ('type' in data && typeof data.type === 'string') {
      // System message types
      if (data.type === 'connected') {
        const sysMsg = data as SystemMessage;
        this.connectionId = sysMsg.connectionId || null;
        this.log('Connection ID:', this.connectionId);

        // Re-subscribe to pending topics
        this.pendingSubscriptions.forEach((topic) => {
          this.sendSubscribeMessage(topic);
        });
        this.pendingSubscriptions = [];

        // Notify connection callbacks
        this.connectionCallbacks.forEach((cb) => cb());
        return;
      }

      if (data.type === 'subscribed') {
        const sysMsg = data as SystemMessage;
        this.log('Subscribed to:', sysMsg.topic);
        return;
      }

      if (data.type === 'unsubscribed') {
        const sysMsg = data as SystemMessage;
        this.log('Unsubscribed from:', sysMsg.topic);
        return;
      }

      if (data.type === 'error') {
        const sysMsg = data as SystemMessage;
        console.error('[Realtime] Error:', sysMsg.message);
        return;
      }

      if (data.type === 'pong') {
        this.log('Heartbeat pong received');
        return;
      }

      // It's a realtime event
      const event = data as RealtimeEvent;
      this.dispatchEvent(event);
    }
  }

  private dispatchEvent(event: RealtimeEvent): void {
    this.log('Event received:', event.type, event.payload);

    // Get the relevant topic from the payload
    const payload = event.payload as Record<string, unknown>;
    const topics = this.getTopicsFromPayload(event.type, payload);

    this.subscriptions.forEach((sub) => {
      // Check if subscription matches
      const topicMatches = topics.includes(sub.topic) || sub.topic === '*';
      const eventTypeMatches = !sub.eventType || sub.eventType === event.type;

      if (topicMatches && eventTypeMatches) {
        try {
          sub.callback(event.payload);
        } catch (error) {
          console.error('[Realtime] Error in event callback:', error);
        }
      }
    });
  }

  private getTopicsFromPayload(eventType: string, payload: Record<string, unknown>): string[] {
    const topics: string[] = [];

    if (payload.workspaceId) {
      topics.push(`workspace:${payload.workspaceId}`);
    }

    if (payload.conversationId) {
      topics.push(`conversation:${payload.conversationId}`);
    }

    if (payload.pipelineId) {
      topics.push(`pipeline:${payload.pipelineId}`);
    }

    if (payload.taskId) {
      topics.push(`task:${payload.taskId}`);
    }

    return topics;
  }

  private sendMessage(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.log('Cannot send message, socket not open');
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.sendMessage({ type: ClientMessageType.PING });
    }, this.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log('Max reconnect attempts reached');
      return;
    }

    // Exponential backoff with jitter
    const delay = Math.min(
      this.reconnectBaseDelay * Math.pow(2, this.reconnectAttempts) + Math.random() * 1000,
      30000
    );

    this.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  disconnect(): void {
    this.isManualDisconnect = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHeartbeat();

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.connectionId = null;
    this.log('Disconnected manually');
  }

  subscribe(topic: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.sendSubscribeMessage(topic);
    } else {
      // Queue for when connected
      if (!this.pendingSubscriptions.includes(topic)) {
        this.pendingSubscriptions.push(topic);
      }
    }
  }

  unsubscribe(topic: string): void {
    this.sendUnsubscribeMessage(topic);
    this.pendingSubscriptions = this.pendingSubscriptions.filter((t) => t !== topic);
  }

  /**
   * Send subscribe message in the format expected by the server.
   * Parses topic format (e.g., 'workspace:xxx') and sends typed message.
   */
  private sendSubscribeMessage(topic: string): void {
    const [type, id] = topic.split(':');
    if (type === 'workspace' && id) {
      this.sendMessage({ type: 'subscribe.workspace', workspaceId: id } as ClientMessage);
    } else if (type === 'conversation' && id) {
      this.sendMessage({ type: 'subscribe.conversation', conversationId: id } as ClientMessage);
    } else {
      this.log('Unknown topic format for subscribe:', topic);
    }
  }

  /**
   * Send unsubscribe message in the format expected by the server.
   * Parses topic format (e.g., 'workspace:xxx') and sends typed message.
   */
  private sendUnsubscribeMessage(topic: string): void {
    const [type, id] = topic.split(':');
    if (type === 'workspace' && id) {
      this.sendMessage({ type: 'unsubscribe.workspace', workspaceId: id } as ClientMessage);
    } else if (type === 'conversation' && id) {
      this.sendMessage({ type: 'unsubscribe.conversation', conversationId: id } as ClientMessage);
    }
    // Silently ignore malformed topics (e.g. workspace switch with empty ID)
  }

  on<T = unknown>(
    topic: string,
    callback: EventCallback<T>,
    eventType?: RealtimeEventTypeValue
  ): () => void {
    const subscription: Subscription = {
      topic,
      eventType,
      callback: callback as EventCallback,
    };

    this.subscriptions.push(subscription);

    // Return unsubscribe function
    return () => {
      const index = this.subscriptions.indexOf(subscription);
      if (index > -1) {
        this.subscriptions.splice(index, 1);
      }
    };
  }

  off(topic: string, eventType?: RealtimeEventTypeValue): void {
    this.subscriptions = this.subscriptions.filter(
      (sub) => !(sub.topic === topic && (!eventType || sub.eventType === eventType))
    );
  }

  onConnect(callback: ConnectionCallback): () => void {
    this.connectionCallbacks.push(callback);
    return () => {
      const index = this.connectionCallbacks.indexOf(callback);
      if (index > -1) {
        this.connectionCallbacks.splice(index, 1);
      }
    };
  }

  onDisconnect(callback: ConnectionCallback): () => void {
    this.disconnectionCallbacks.push(callback);
    return () => {
      const index = this.disconnectionCallbacks.indexOf(callback);
      if (index > -1) {
        this.disconnectionCallbacks.splice(index, 1);
      }
    };
  }

  // Typing indicator methods
  startTyping(conversationId: string): void {
    this.sendMessage({
      type: ClientMessageType.TYPING_START,
      conversationId,
    });
  }

  stopTyping(conversationId: string): void {
    this.sendMessage({
      type: ClientMessageType.TYPING_STOP,
      conversationId,
    });
  }

  // Connection status
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getConnectionId(): string | null {
    return this.connectionId;
  }

  // ============================================================
  // Workspace Switch Helpers
  // ============================================================

  /**
   * Safely disconnect the WebSocket if connected.
   * Idempotent - safe to call even if already disconnected.
   * Clears all pending subscriptions and local state.
   */
  disconnectSafely(): void {
    this.log('disconnectSafely called');

    // Clear pending subscriptions to prevent stale workspace subscriptions
    this.pendingSubscriptions = [];

    // If already disconnected, nothing to do
    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      this.log('Already disconnected');
      return;
    }

    // Use existing disconnect logic
    this.disconnect();
  }

  /**
   * Force a fresh WebSocket connection.
   * Creates a new WebSocket instance, discarding any existing connection.
   * Returns a Promise that resolves when the connection is established.
   */
  reconnect(): Promise<void> {
    this.log('reconnect called - forcing fresh connection');

    // Ensure clean disconnect first
    this.disconnectSafely();

    // Reset reconnect counter for fresh start
    this.reconnectAttempts = 0;

    // Return promise that resolves when connected
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket reconnect timeout'));
      }, 10000);

      const cleanup = this.onConnect(() => {
        clearTimeout(timeout);
        cleanup();
        resolve();
      });

      // Initiate connection
      this.connect();
    });
  }

  /**
   * Perform a complete workspace switch with connection reset.
   * This is the recommended method for switching workspaces.
   *
   * Steps:
   * 1. Disconnect existing WebSocket (clears all subscriptions server-side)
   * 2. Reconnect with fresh WebSocket instance
   * 3. Subscribe to the new workspace
   *
   * @param newWorkspaceId - The workspace ID to switch to
   * @returns Promise that resolves when connected and subscribed to new workspace
   */
  async switchWorkspace(newWorkspaceId: string): Promise<void> {
    this.log(`Switching workspace to ${newWorkspaceId}`);

    // Step 1: Disconnect (server will clean up all subscriptions)
    this.disconnectSafely();

    // Step 2: Reconnect with fresh connection
    await this.reconnect();

    // Step 3: Subscribe to new workspace
    this.subscribeWorkspace(newWorkspaceId);

    this.log(`Workspace switch complete: ${newWorkspaceId}`);
  }

  /**
   * Subscribe to a workspace channel.
   * Convenience method that handles the topic format.
   */
  subscribeWorkspace(workspaceId: string): void {
    this.subscribe(`workspace:${workspaceId}`);
  }

  /**
   * Unsubscribe from a workspace channel.
   * Convenience method that handles the topic format.
   */
  unsubscribeWorkspace(workspaceId: string): void {
    this.unsubscribe(`workspace:${workspaceId}`);
  }
}

// Singleton instance
export const realtimeClient = new RealtimeClient({
  debug: process.env.NODE_ENV === 'development',
});

export { RealtimeClient };
